import React, { useState, useEffect } from 'react';
import { Plus, MapPin, Briefcase, Users, Clock, Eye, ClipboardList } from 'lucide-react';
import { Link } from 'react-router-dom';
import { fetchMyJobs } from '../services/api';
import { useApp } from '../context/AppContext';
import { JOB_TYPE_LABELS, labelOf, jobTitle } from '../constants/jobOptions';
import ApplicationsSheet from '../components/ApplicationsSheet';
import './InsertePage.css';

export default function InsertePage() {
  const { user, openAuth } = useApp();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJob, setSelectedJob] = useState(null);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    fetchMyJobs()
      .then(({ data }) => {
        const list = Array.isArray(data) ? data : data.jobs || data.data || [];
        setJobs(list);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="inserate-gate">
        <div className="inserate-gate__content">
          <div className="inserate-gate__icon"><ClipboardList size={56} strokeWidth={1.2} /></div>
          <h2>Inserate verwalten</h2>
          <p>Melde dich an, um deine Job-Inserate zu verwalten.</p>
          <button className="inserate-gate__btn" onClick={() => openAuth('login')}>
            Anmelden
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="inserate-page">
      {/* Header */}
      <div className="inserate-header">
        <div>
          <h1>Meine Inserate</h1>
          <p className="inserate-header__sub">{jobs.length} aktive Inserate</p>
        </div>
        <Link to="/post-job" className="inserate-new-btn">
          <Plus size={18} />
          Neu
        </Link>
      </div>

      {/* Stats */}
      <div className="inserate-stats">
        <div className="inserate-stat">
          <Briefcase size={20} />
          <div>
            <strong>{jobs.length}</strong>
            <span>Inserate</span>
          </div>
        </div>
        <div className="inserate-stat">
          <Users size={20} />
          <div>
            <strong>{jobs.reduce((s, j) => s + (j.applicant_count || j.applicants_count || 0), 0)}</strong>
            <span>Bewerbungen</span>
          </div>
        </div>
        <div className="inserate-stat">
          <Eye size={20} />
          <div>
            <strong>{jobs.reduce((s, j) => s + (j.views || 0), 0)}</strong>
            <span>Aufrufe</span>
          </div>
        </div>
      </div>

      {/* Job list */}
      <div className="inserate-list">
        {loading && (
          <div className="inserate-loading">
            <div className="inserate-loading__spinner" />
            <p>Lädt...</p>
          </div>
        )}

        {!loading && jobs.length === 0 && (
          <div className="inserate-empty">
            <div className="inserate-empty__emoji"><ClipboardList size={48} strokeWidth={1.2} /></div>
            <h3>Noch keine Inserate</h3>
            <p>Schalte dein erstes Inserat und finde das perfekte Talent.</p>
            <Link to="/post-job" className="inserate-empty__btn">
              <Plus size={16} /> Inserat erstellen
            </Link>
          </div>
        )}

        {!loading && jobs.map(job => (
          <div key={job.id} className="inserate-item">
            <div className="inserate-item__header">
              <div className="inserate-item__logo">
                {(job.company || 'J').slice(0, 2).toUpperCase()}
              </div>
              <div className="inserate-item__info">
                <h3 className="inserate-item__title">{jobTitle(job.title)}</h3>
                <p className="inserate-item__meta">
                  {job.location && <span><MapPin size={12} /> {job.location}</span>}
                  {job.job_type && <span><Briefcase size={12} /> {labelOf(JOB_TYPE_LABELS, job.job_type)}</span>}
                </p>
              </div>
              <span className={`inserate-status inserate-status--${job.status === 'active' || !job.status ? 'active' : 'inactive'}`}>
                {job.status === 'inactive' ? 'Inaktiv' : 'Aktiv'}
              </span>
            </div>
            <div className="inserate-item__footer">
              <span className="inserate-item__applicants">
                <Users size={13} /> {job.applicant_count || job.applicants_count || 0} Bewerbungen
              </span>
              <span className="inserate-item__posted">
                <Clock size={13} /> {job.posted || 'Kürzlich'}
              </span>
              <Link to={`/post-job/${job.id}`} className="inserate-item__edit-btn">
                Bearbeiten
              </Link>
              <button
                className="inserate-item__apps-btn"
                onClick={() => setSelectedJob(job)}
              >
                Bewerbungen →
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* CTA */}
      {!loading && (
        <div className="inserate-cta">
          <Link to="/post-job" className="inserate-cta__btn">
            <Plus size={18} /> Neues Inserat erstellen
          </Link>
        </div>
      )}

      {selectedJob && (
        <ApplicationsSheet job={selectedJob} onClose={() => setSelectedJob(null)} />
      )}
    </div>
  );
}
